import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  InputAdornment,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../auth/AuthContext";
import useGraphQLClient from "../../hooks/useGraphQLClient";
import { UserRole } from "../../constants/userRoles.ts";

import { CREATE_USER } from "../../graph/users/mutations";

const tenantRoleOptions = [
  { value: UserRole.TENANT_ADMIN, label: "Tenant Admin" },
  { value: UserRole.TENANT_STAFF, label: "Tenant Staff" },
];

const CreateUserForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();
  const { user } = useAuth();

  const isTenantAdmin = user?.role === UserRole.TENANT_ADMIN;
  const redirectPath = location.state?.redirectPath || "/manageUsers";

  const handleFormSubmit = async (
    values,
    { resetForm, setSubmitting }
  ) => {
    if (!tenantId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Tenant could not be resolved",
      });

      setSubmitting(false);
      return;
    }

    const role = isTenantAdmin
      ? UserRole.TENANT_STAFF
      : values.role;

    try {
      const input = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim().toLowerCase(),
        password: values.password,
        role,
        tenantID: tenantId,
        hourlyRate:
          values.hourlyRate === "" ||
          values.hourlyRate === null ||
          values.hourlyRate === undefined
            ? null
            : Number(values.hourlyRate),
        invitedBy: values.invitedBy.trim() || null,
        notes: values.notes.trim() || null,
      };

      await client.request(CREATE_USER, { input });

      setSnackbar({
        open: true,
        message: "User created successfully",
        severity: "success",
      });

      resetForm();
      navigate(redirectPath);
    } catch (err) {
      const message =
        err?.response?.errors?.[0]?.message ||
        "Unexpected error";

      console.error("GraphQL Error:", err);

      setSnackbar({
        open: true,
        message: `Failed to create user: ${message}`,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="CREATE USER"
          subtitle="Tenant could not be resolved"
        />

        <Typography mt={3}>
          A tenant must be available before creating a user.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="CREATE USER"
        subtitle={`Create a user for ${
          tenantName || "selected tenant"
        }`}
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="First Name"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.firstName && errors.firstName
                )}
                helperText={
                  touched.firstName && errors.firstName
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Last Name"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.lastName && errors.lastName
                )}
                helperText={
                  touched.lastName && errors.lastName
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Password"
                name="password"
                type="password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.password && errors.password
                )}
                helperText={
                  touched.password && errors.password
                }
                sx={{ gridColumn: "span 2" }}
              />

              {isTenantAdmin ? (
                <TextField
                  label="Role"
                  value="Tenant Staff"
                  disabled
                  helperText="Tenant admins can only create tenant staff users"
                  sx={{ gridColumn: "span 2" }}
                />
              ) : (
                <FormControl
                  fullWidth
                  sx={{ gridColumn: "span 2" }}
                  error={Boolean(
                    touched.role && errors.role
                  )}
                >
                  <InputLabel>Role</InputLabel>

                  <Select
                    name="role"
                    value={values.role}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Role"
                  >
                    {tenantRoleOptions.map((roleOption) => (
                      <MenuItem
                        key={roleOption.value}
                        value={roleOption.value}
                      >
                        {roleOption.label}
                      </MenuItem>
                    ))}
                  </Select>

                  {touched.role && errors.role && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75 }}
                    >
                      {errors.role}
                    </Typography>
                  )}
                </FormControl>
              )}

              <TextField
                label="Hourly Rate"
                name="hourlyRate"
                type="number"
                value={values.hourlyRate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.hourlyRate && errors.hourlyRate
                )}
                helperText={
                  touched.hourlyRate && errors.hourlyRate
                    ? errors.hourlyRate
                    : "Optional internal labour cost per hour"
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        £
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    min: 0,
                    step: "0.01",
                  },
                }}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Invited By"
                name="invitedBy"
                value={values.invitedBy}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText="Optional"
                sx={{ gridColumn: "span 2" }}
              />

              <MarkdownEditor
                value={values.notes}
                onChange={(nextValue) =>
                  setFieldValue("notes", nextValue)
                }
                label="Notes"
                minRows={14}
                error={Boolean(
                  touched.notes && errors.notes
                )}
                helperText={
                  touched.notes && errors.notes
                }
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box
              display="flex"
              justifyContent="end"
              mt="20px"
            >
              <ActionButton
                type="submit"
                tone="success"
                disabled={isSubmitting}
              >
                Create User
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required("Required"),

  lastName: yup
    .string()
    .trim()
    .required("Required"),

  email: yup
    .string()
    .email("Invalid email")
    .required("Required"),

  password: yup
    .string()
    .min(8, "Minimum 8 characters")
    .required("Required"),

  role: yup
    .string()
    .oneOf([
      UserRole.TENANT_ADMIN,
      UserRole.TENANT_STAFF,
    ])
    .required("Required"),

  hourlyRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .nullable()
    .min(0, "Hourly rate cannot be negative")
    .typeError("Hourly rate must be a valid number"),

  invitedBy: yup.string().nullable(),

  notes: yup.string().nullable(),
});

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  role: UserRole.TENANT_STAFF,
  hourlyRate: "",
  invitedBy: "",
  notes: "",
};

export default CreateUserForm;